import {
    Availability,
    LinkedOfficeFragment,
    OfficeType,
    PropertyType,
    SizeUnit,
    TermUnit
} from 'src/generated/graphql';
import { getTotalSize } from 'src/components/OfficeItem/lib';
import { convertDurationToDays } from '.';
import { convertAreaUnits } from './size';

export const propertyTypeOfficeTypes = {
    [PropertyType.Lease]: [OfficeType.Lease, OfficeType.Sublease, OfficeType.Assignment],
    [PropertyType.Flexible]: [OfficeType.Serviced, OfficeType.Hotdesk],
    [PropertyType.Managed]: [OfficeType.Managed],
    [PropertyType.Purchase]: [OfficeType.Purchase],
};

function max(value: undefined | number) {
    if (value === undefined) {
        return undefined;
    }
    if (value > 0) {
        return value;
    }
    return undefined;
}

export const getLatestCreatedDate = (
    offices: { createdAt?: string }[]
): number | undefined => (
    max(Math.max(...offices.map((office) => new Date(office?.createdAt || '').getTime())))
);

export const getMaxDesks = (
    offices: {
        desks?: number | null | undefined,
        available: Availability
    }[]
): number | undefined => max(Math.max(...offices.filter(({ available }) => available === Availability.Available
    || available === Availability.Comingsoon)
    .map((office) => (
        office.desks || 0
    ))));
export const getMinDesks = (
    offices: {
        desks?: number | null | undefined,
        available: Availability
    }[]
): number | undefined => max(Math.min(...offices.filter(({ available }) => available === Availability.Available
    || available === Availability.Comingsoon)
    .map((office) => (
        office.desks || 0
    ))));

export const getMinDesksResults = (
    offices: {
        desks?: number | null | undefined
        linkedToOffice?: LinkedOfficeFragment[];
        linkedByOffice?: LinkedOfficeFragment[];
    }[]
): number | undefined => {
    let total = 0;
    if (offices.length === 1) {
        total += offices[0].desks || 0;
        if ((offices[0].linkedToOffice?.length || 0) > 0) {
            total += offices[0].linkedToOffice?.reduce(
                (num: number, next: LinkedOfficeFragment) => (
                    num + (
                        next.desks || 0
                    )
                ),
                0
            ) || 0;
            return total;
        }
        return total;
    }

    if (offices.length > 1) {
        let hasLinkedOffice = false;
        let linkedOfficeMin = 0;
        let convertedSize = offices[0].desks || 0;
        offices.forEach((office) => {
            convertedSize = convertedSize > (office.desks || 0) ? office.desks || 0 : convertedSize;
            if ((office.linkedByOffice?.length || 0) === 0) {
                if ((office.linkedToOffice?.length || 0) > 0) {
                    hasLinkedOffice = true;
                    let linkedTotal = convertedSize || 0;
                    linkedTotal += office.linkedToOffice?.reduce(
                        (num: number, next: LinkedOfficeFragment) => (
                            num + (
                                next.desks || 0
                            )
                        ),
                        0
                    ) || 0;
                    linkedOfficeMin = linkedTotal;
                }
            }
        });

        return hasLinkedOffice ? linkedOfficeMin : convertedSize;
    }
    return total;
};

export const getMaxDesksResults = (
    offices: {
        desks?: number | null | undefined
        linkedToOffice?: LinkedOfficeFragment[];
        linkedByOffice?: LinkedOfficeFragment[];
    }[]
): number | undefined => {
    let total = 0;
    if (offices.length === 1) {
        total += offices[0].desks || 0;
        if ((offices[0].linkedToOffice?.length || 0) > 0) {
            total += offices[0].linkedToOffice?.reduce(
                (num: number, next: LinkedOfficeFragment) => (
                    num + (
                        next.desks || 0
                    )
                ),
                0
            ) || 0;
            return total;
        }
        return total;
    }

    if (offices.length > 1) {
        let hasLinkedOffice = false;
        let linkedOfficeMax = 0;
        let convertedSize = 0;
        offices.forEach((office) => {
            convertedSize = convertedSize < (office.desks || 0) ? office.desks || 0 : convertedSize;
            if ((office.linkedByOffice?.length || 0) === 0) {
                if ((office.linkedToOffice?.length || 0) > 0) {
                    hasLinkedOffice = true;
                    let linkedTotal = convertedSize || 0;
                    linkedTotal += office.linkedToOffice?.reduce(
                        (num: number, next: LinkedOfficeFragment) => (
                            num + (
                                next.desks || 0
                            )
                        ),
                        0
                    ) || 0;
                    linkedOfficeMax = linkedTotal;
                }
            }
        });

        return hasLinkedOffice ? linkedOfficeMax : convertedSize;
    }
    return total;
};

export const getMinSize = (
    offices: {
        size?: number | null | undefined;
        sizeUnit: SizeUnit;
        linkedToOffice?: LinkedOfficeFragment[];
        linkedByOffice?: LinkedOfficeFragment[];
    }[],
    type: PropertyType,
    sizeFilter: SizeUnit,
): number | undefined => {
    let total = 0;
    if (offices.length === 1) {
        total += offices[0].size && offices[0].sizeUnit !== sizeFilter
            ? convertAreaUnits(offices?.[0].size, offices[0].sizeUnit) || 0
            : offices[0].size || 0;
        if ((offices[0].linkedToOffice?.length || 0) > 0) {
            total += offices[0].linkedToOffice?.reduce(
                (num: number, next: LinkedOfficeFragment) => (
                    num + (
                        next?.size && next.sizeUnit !== sizeFilter
                            ? convertAreaUnits(next.size, next.sizeUnit) || 0
                            : next.size || 0
                    )
                ),
                0
            ) || 0;
            return total;
        }
        return total;
    }

    if (offices.length > 1) {
        let hasLinkedOffice = false;
        let linkedOfficeMin = 0;
        let convertedSize = 0;
        total = offices[0].size || 0;
        offices.forEach((office) => {
            convertedSize = office.size && sizeFilter !== office.sizeUnit
                ? convertAreaUnits(office.size, office.sizeUnit) || 0
                : office.size || 0;
            total = total < convertedSize ? total : convertedSize;
            if ((office.linkedByOffice?.length || 0) === 0) {
                if ((office.linkedToOffice?.length || 0) > 0) {
                    hasLinkedOffice = true;
                    let linkedTotal = convertedSize || 0;
                    linkedTotal += office.linkedToOffice?.reduce(
                        (num: number, next: LinkedOfficeFragment) => (
                            num + (
                                next?.size && next.sizeUnit !== sizeFilter
                                    ? convertAreaUnits(next.size, next.sizeUnit) || 0
                                    : next.size || 0
                            )
                        ),
                        0
                    ) || 0;
                    linkedOfficeMin = linkedTotal;
                }
            }
        });

        return hasLinkedOffice ? linkedOfficeMin : total;
    }
    return total;
};

export const getMaxSize = (
    offices: {
        size?: number | null | undefined;
        sizeUnit: SizeUnit;
        linkedToOffice?: LinkedOfficeFragment[];
        linkedByOffice?: LinkedOfficeFragment[];
    }[],
    type: PropertyType,
    sizeFilter: SizeUnit,
): number | undefined => {
    let total = 0;
    if (offices.length === 1) {
        total += offices[0].size && offices[0].sizeUnit !== sizeFilter
            ? convertAreaUnits(offices?.[0].size, offices[0].sizeUnit) || 0
            : offices[0].size || 0;
        if ((offices[0].linkedToOffice?.length || 0) > 0) {
            total += offices[0].linkedToOffice?.reduce(
                (num: number, next: LinkedOfficeFragment) => (
                    num + (
                        next?.size && next.sizeUnit !== sizeFilter
                            ? convertAreaUnits(next.size, next.sizeUnit) || 0
                            : next.size || 0
                    )
                ),
                0
            ) || 0;
            return total;
        }
        return total;
    }

    if (offices.length > 1) {
        let hasLinkedOffice = false;
        let linkedOfficeMax = 0;
        let convertedSize = 0;
        offices.forEach((office) => {
            convertedSize = office.size && sizeFilter !== office.sizeUnit
                ? convertAreaUnits(office.size, office.sizeUnit) || 0
                : office.size || 0;
            total = total > convertedSize ? total : convertedSize;
            if ((office.linkedByOffice?.length || 0) === 0) {
                if ((office.linkedToOffice?.length || 0) > 0) {
                    hasLinkedOffice = true;
                    let linkedTotal = convertedSize || 0;
                    linkedTotal += office.linkedToOffice?.reduce(
                        (num: number, next: LinkedOfficeFragment) => (
                            num + (
                                next?.size && next.sizeUnit !== sizeFilter
                                    ? convertAreaUnits(next.size, next.sizeUnit) || 0
                                    : next.size || 0
                            )
                        ),
                        0
                    ) || 0;
                    linkedOfficeMax = linkedTotal;
                }
            }
        });

        return hasLinkedOffice ? linkedOfficeMax : total;
    }
    return total;
};

export const getMinCost = (offices: {
    totalCost?: number | null | undefined,
    available: Availability
}[]): number | undefined => (
    max(Math.min(...offices.filter(({ available }) => (
        available === Availability.Available)).map((office) => office.totalCost || 0)))
);

export const getMinLeaseCost = (
    offices: {
        price?: number | null | undefined,
        size?: number | null | undefined;
        sizeUnit: SizeUnit,
        linkedToOffice?: LinkedOfficeFragment[],
    }[],
    sizeUnit: SizeUnit = SizeUnit.SquareFeet,
): number | undefined => (
    max(
        Math.min(...offices.map((office) => {
            const sizeUnitMatches = sizeUnit !== office.sizeUnit;
            const convertedSize = office.size && sizeUnitMatches
                ? convertAreaUnits(office.size, office.sizeUnit)
                : office.size || 0;
            const totalSizes = getTotalSize(0, convertedSize || 0, sizeUnit, office.linkedToOffice);
            let price = office.price || 0;

            if (office.linkedToOffice?.length) {
                price *= (office.size || 0);
                price = office.linkedToOffice?.reduce((sum, linkedOffice) => (
                    sum + (linkedOffice.price || 0) * (linkedOffice.size || 0)
                ), price * (office.size || 0));
                const sizes = totalSizes.size > 0 ? totalSizes.size : 1;
                price /= sizes;
            }

            return price && sizeUnitMatches ? convertAreaUnits(price, sizeUnit) || 0 : price || 0;
        }))
    )
);

export const getMinTermIndex = (
    offices: { minimumTerm?: number | null | undefined; minimumTermUnit: TermUnit }[]
): number => {
    const miiin = offices?.map((office) => convertDurationToDays(office?.minimumTerm || 0, office.minimumTermUnit));
    const miin = Math.min(...miiin);
    const indx = miiin.findIndex((i) => i === miin);
    return indx;
};
export const getMaxTermIndex = (
    offices: { minimumTerm?: number | null | undefined; minimumTermUnit: TermUnit }[]
): number => {
    const miiin = offices?.map((office) => convertDurationToDays(office?.minimumTerm || 0, office.minimumTermUnit));
    const miin = Math.max(...miiin);
    const indx = miiin.findIndex((i) => i === miin);
    return indx;
};
